import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useLayoutEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { useLocation } from 'react-router-dom';
import { createReloadViewStore } from './reloadViewState';

const store = createReloadViewStore(window.history, (performance.getEntriesByType?.('navigation')[0] as PerformanceNavigationTiming | undefined)?.type === 'reload');

/** Persist navigation context, never form fields, permissions or pending actions. */
export function useReloadViewState<T>(key: string, initial: T | (() => T), valid: (value: unknown) => value is T): [T, Dispatch<SetStateAction<T>>] {
  const client = useQueryClient();
  const owner = String(client.getQueryData<{ id: number }>(['current-user'])?.id ?? 'guest');
  const location = useLocation();
  const href = location.pathname + location.search + location.hash;
  const [value, setValue] = useState<T>(() => {
    const saved = store.read(href, owner, key, valid);
    return saved !== undefined ? saved : typeof initial === 'function' ? (initial as () => T)() : initial;
  });
  useLayoutEffect(() => { store.claim(owner, key); }, [owner, key]);
  useEffect(() => { store.write(href, owner, key, value); }, [href, owner, key, value]);
  useEffect(() => () => { store.write(href, owner, key, undefined, true); }, [href, owner, key]);
  return [value, setValue];
}
